import { clsx } from 'clsx'
import { ReactNode } from 'react'
import { useId } from '@floating-ui/react'
import { Spinner } from './Spinner'

type Props = {
  children?: ReactNode
  title?: ReactNode
  tip?: ReactNode
  subtitle?: ReactNode
  buttons?: ReactNode
  className?: string
  loading?: boolean
}
export function Dialog({ children, title, tip, subtitle, buttons, className, loading, ...props }: Props) {
  const labelId = useId()
  const descriptionId = useId()

  return (
    <div
      role="dialog"
      aria-labelledby={title ? labelId : undefined}
      aria-describedby={subtitle ? descriptionId : undefined}
      className={clsx(
        'flex h-svh w-svw flex-col items-stretch gap-2 overflow-hidden bg-white px-6 py-6 outline-none sm:my-4 sm:h-auto sm:rounded-md sm:shadow-md [:where(&)]:sm:max-w-xl',
        className
      )}
      {...props}
    >
      {title && (
        <div className="mb-6 flex flex-col gap-3">
          <h1 id={labelId} className="text-2xl text-gray-800">
            {title}
          </h1>
          {subtitle && (
            <h2 id={descriptionId} className="text-base text-gray-700">
              {subtitle}
            </h2>
          )}
          {tip}
        </div>
      )}
      <div className="min-h-0 flex-grow overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner className="size-8" />
          </div>
        ) : (
          children
        )}
      </div>
      {buttons}
    </div>
  )
}

export function DialogButtons({
  children,
  leftButton
}: {
  children?: ReactNode
  leftButton?: ReactNode
}) {
  return (
    <div className="mt-4 flex flex-col-reverse items-stretch justify-end gap-2 sm:flex-row">
      {leftButton}
      {leftButton && <div className="grow" />}
      {children}
    </div>
  )
}

export function DialogTip({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <blockquote className={clsx('rounded-md bg-yellow-100 text-base')}>
      <div className="relative flex flex-col gap-2 px-6 py-6">
        {title && <h3 className="text-base font-bold text-gray-900 text-yellow-800">{title}</h3>}
        <div className="whitespace-pre-wrap text-base text-yellow-800">{children}</div>
      </div>
    </blockquote>
  )
}
